// src/oura/merchant/AnswerCard.tsx
//
// The answer box for one open question, by the kind of question:
//
//   approve        what we will do, then Agree or Change ("What should it say?")
//   choose         the question's options, or a text box with its own hint
//                  and an optional photo ("A list or a photo works")
//   provide_file   a file picker that uploads the file straight away
//
// Nothing is sent from here. `AnswerInput` writes a draft, and the question
// drawer's "Send answer" sends it (QuestionSheet). The drafts are the host's
// (MerchantDrawers), so moving to the next question or closing the drawer
// loses nothing.
//
// `claim` is the drawer's focus claim (useDrawerFocusClaim): the first
// control of the answer takes focus when the question opens, since answering
// is the move. For a choice that is the checked option, else the first.

import { useId, useRef, useState, type Ref } from "react";
import { CheckIcon, FileIcon, UploadIcon } from "lucide-react";
import { uploadFile, UPLOAD_ACCEPT, UPLOAD_MAX_BYTES, type PackItem } from "@service/aeo/clientReview";
import { FOCUS_RING, LATE_TEXT, PillButton, SURFACE, TYPE } from "@oura/ui";
import { cn } from "../lib/cn";
import { useWritesPaused } from "@context/maintenance";
import { useRadioGroup } from "../lib/useRadioGroup";
import type { AnswerDraft } from "./asks";

const FIELD = cn(TYPE.tb, "block w-full resize-y rounded-xl border-0 bg-gray-50 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600");

/** A callback ref that may hand back its cleanup (React 19), as useDrawerFocusClaim's does. */
type ClaimRef = (el: HTMLElement | null) => void | (() => void);

export interface AnswerInputProps {
    item: PackItem;
    draft: AnswerDraft | undefined;
    onDraft: (draft: AnswerDraft | null) => void;
    businessId: string;
    /** A send is running: the answer stays put until it is done. */
    disabled?: boolean;
    /** The drawer's focus claim, put on the answer's first control. */
    claim?: ClaimRef;
    /** Cmd or Ctrl+Enter in a text box: send, when the answer is ready. */
    onSubmit?: () => void;
    /** Agree on an approval sends straight away; Change opens the box. Absent: Agree only marks the pick. */
    onAgree?: () => void;
}

export function AnswerInput({ item, draft, onDraft, businessId, disabled, claim, onSubmit, onAgree }: AnswerInputProps) {
    const set = (patch: AnswerDraft) => onDraft({ ...draft, ...patch });
    const writesPaused = useWritesPaused();
    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && onSubmit && !writesPaused) {
            e.preventDefault();
            onSubmit();
        }
    };
    return (
        <fieldset disabled={disabled} className="m-0 min-w-0 border-0 p-0">
            <legend className="sr-only">Your answer to {item.title}</legend>
            {item.kind === "approve" && <Approve item={item} draft={draft} set={set} claim={claim} onKeyDown={onKeyDown} onAgree={onAgree} />}
            {item.kind === "choose" &&
                (item.options && item.options.length > 0 ? (
                    <Options label={item.title} options={item.options} value={draft?.option} onPick={(option) => set({ option })} claim={claim} />
                ) : (
                    <>
                        <textarea
                            ref={claim as Ref<HTMLTextAreaElement>}
                            rows={3}
                            value={draft?.text ?? ""}
                            onChange={(e) => set({ text: e.target.value })}
                            onKeyDown={onKeyDown}
                            placeholder={item.placeholder || "Type your answer"}
                            aria-label={item.title}
                            className={cn(FIELD, "mt-3")}
                        />
                        {/* A photo goes with the answer, or is the answer: a price list, a menu, a sign. */}
                        <FilePick businessId={businessId} file={draft?.file} onFile={(file) => set({ file })} label="Add a photo" optional />
                    </>
                ))}
            {item.kind === "provide_file" && <FilePick businessId={businessId} file={draft?.file} onFile={(file) => set({ file })} claim={claim} />}
        </fieldset>
    );
}

/** An approval: what we will do, then Agree or Change. */
function Approve({
    item,
    draft,
    set,
    claim,
    onKeyDown,
    onAgree,
}: {
    item: PackItem;
    draft: AnswerDraft | undefined;
    set: (patch: AnswerDraft) => void;
    claim?: ClaimRef;
    onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    onAgree?: () => void;
}) {
    const pick = draft?.pick;
    const changeBox = useRef<HTMLTextAreaElement>(null);
    const change = () => {
        set({ pick: "change" });
        // The box renders on this pick; focus it once it is there.
        requestAnimationFrame(() => changeBox.current?.focus());
    };
    return (
        <>
            {item.proposed && (
                <div className={cn(SURFACE.inner, "mt-3")}>
                    <p className={cn(TYPE.lab, "m-0")}>We have</p>
                    <p className={cn(TYPE.tb, "m-0 mt-0.5 whitespace-pre-line font-medium text-gray-900")}>{item.proposed}</p>
                </div>
            )}
            {item.proposed && item.current && (
                <details className="group mt-2">
                    <summary className={cn(TYPE.tc, "inline-flex cursor-pointer list-none rounded py-1 font-medium text-blue-600 [&::-webkit-details-marker]:hidden", FOCUS_RING)}>
                        <span className="group-open:hidden">What is there now</span>
                        <span className="hidden group-open:inline">Hide what is there now</span>
                    </summary>
                    <p className={cn(TYPE.tb, "m-0 mt-2 whitespace-pre-line rounded-xl bg-gray-50 px-3 py-2.5 text-gray-600")}>{item.current}</p>
                </details>
            )}
            {!item.proposed && item.outline && item.outline.length > 0 && (
                <ul className={cn(TYPE.tb, "list-plain m-0 mt-3 space-y-1.5 p-0 text-gray-700")}>
                    {item.outline.map((line) => (
                        <li key={line} className="flex gap-2.5">
                            <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" />
                            {line}
                        </li>
                    ))}
                </ul>
            )}
            <div
                className="mt-3 flex flex-wrap gap-2"
                // Agree is the pill that takes focus: the kit's pill takes no ref of its own.
                ref={(el) => {
                    const agree = el?.querySelector("button");
                    return agree && claim ? claim(agree) : undefined;
                }}
            >
                <PillButton
                    writes variant={pick === "agree" ? "primary" : "secondary"}
                    icon={CheckIcon}
                    aria-pressed={pick === "agree"}
                    onClick={() => (onAgree ? onAgree() : set({ pick: "agree" }))}
                >
                    Agree
                </PillButton>
                <PillButton variant={pick === "change" ? "primary" : "secondary"} aria-pressed={pick === "change"} onClick={change}>
                    Change
                </PillButton>
            </div>
            {pick === "change" && (
                <textarea
                    ref={changeBox}
                    rows={3}
                    value={draft?.text ?? ""}
                    onChange={(e) => set({ text: e.target.value })}
                    onKeyDown={onKeyDown}
                    placeholder="What should it say?"
                    aria-label={`What should "${item.title}" say?`}
                    className={cn(FIELD, "mt-3")}
                />
            )}
        </>
    );
}

/** The question's options. One pick; picking another replaces it. Also the doc answer's choice (drawers/DocAnswer.tsx). */
export function Options({
    label,
    options,
    value,
    onPick,
    claim,
}: {
    label: string;
    options: string[];
    value: string | undefined;
    onPick: (option: string) => void;
    claim?: ClaimRef;
}) {
    const radio = useRadioGroup(options, value, onPick);
    return (
        <div role="radiogroup" aria-label={label} className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            {options.map((option, i) => {
                const on = value === option;
                const { ref, ...props } = radio(i);
                // The tab stop (the checked option, else the first) is the one that takes focus.
                const stop = props.tabIndex === 0;
                return (
                    <button
                        key={option}
                        type="button"
                        {...props}
                        ref={(el) => {
                            ref(el);
                            const release = stop && claim ? claim(el) : undefined;
                            return () => {
                                ref(null);
                                if (typeof release === "function") release();
                            };
                        }}
                        className={cn(
                            TYPE.tb,
                            "flex min-h-11 items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-gray-900 ring-inset transition-colors sm:min-h-0",
                            on ? "bg-blue-50 ring-2 ring-blue-600" : "bg-white ring-1 ring-gray-200 hover:bg-gray-50",
                            FOCUS_RING,
                        )}
                    >
                        <span
                            aria-hidden
                            className={cn(
                                "h-4 w-4 shrink-0 rounded-full border-[1.5px]",
                                on ? "border-blue-600 bg-[radial-gradient(circle,#2563eb_0_4px,#fff_4.5px)]" : "border-gray-400",
                            )}
                        />
                        {option}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * A file answer. The file uploads the moment it is chosen and the draft keeps
 * the key the upload returned, which is what the decision sends.
 */
function FilePick({
    businessId,
    file,
    onFile,
    claim,
    label = "Choose a file",
    optional = false,
}: {
    businessId: string;
    file: AnswerDraft["file"];
    onFile: (file: AnswerDraft["file"]) => void;
    claim?: ClaimRef;
    /** The picker's words before a file is chosen. */
    label?: string;
    /** Beside a text answer: the file goes with the words, and can be taken off again. */
    optional?: boolean;
}) {
    const inputId = useId();
    const [uploading, setUploading] = useState(false);
    const writesPaused = useWritesPaused();
    const [problem, setProblem] = useState<string | null>(null);

    const choose = async (picked: File) => {
        if (picked.size > UPLOAD_MAX_BYTES) {
            setProblem("That file is over 20 MB.");
            return;
        }
        setProblem(null);
        setUploading(true);
        try {
            const up = await uploadFile(businessId, picked);
            onFile({ fileKey: up.fileKey, name: up.name });
        } catch {
            setProblem("That file did not upload. Try again.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="mt-3">
            <input
                ref={claim as Ref<HTMLInputElement>}
                id={inputId}
                type="file"
                accept={UPLOAD_ACCEPT}
                disabled={uploading || writesPaused}
                className="peer sr-only"
                onChange={(e) => {
                    const picked = e.target.files?.[0];
                    e.target.value = "";
                    if (picked) void choose(picked);
                }}
            />
            <label
                htmlFor={inputId}
                className={cn(
                    TYPE.tc,
                    "relative inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full px-4 font-medium ring-1 ring-inset transition-colors after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-[''] peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-blue-600",
                    file ? "bg-emerald-50 text-emerald-800 ring-emerald-200" : "bg-white text-gray-900 ring-black/[0.08] hover:bg-gray-50",
                    uploading && "cursor-wait opacity-60",
                )}
            >
                {file ? <FileIcon aria-hidden size={14} /> : <UploadIcon aria-hidden size={14} />}
                <span className="max-w-[16rem] truncate">{uploading ? "Uploading…" : file ? file.name : label}</span>
            </label>
            {file && !uploading && !optional && <span className={cn(TYPE.tm, "ml-2 text-gray-500")}>Ready to send. Choose again to replace it.</span>}
            {file && !uploading && optional && (
                <button
                    type="button"
                    onClick={() => onFile(undefined)}
                    aria-label={`Remove ${file.name}`}
                    className={cn(TYPE.tc, "relative ml-2 rounded font-medium text-blue-600 hover:text-blue-700 after:absolute after:-inset-x-1 after:-inset-y-[13px] after:content-['']", FOCUS_RING)}
                >
                    Remove
                </button>
            )}
            {problem && (
                <p role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-1.5")}>
                    {problem}
                </p>
            )}
        </div>
    );
}
